import { useCallback, useEffect, useRef } from "react";
import { t } from "i18next";
import axios, { AxiosResponse } from "axios";
import { ServerResponse } from "../../../types";
import { StripePaymentConfigTypes } from "../components/StripePayment";
import { webPurchase } from "../api/paymentApi";
import { usePaymentMutation } from "./usePaymentMutation";
import { useUserData } from "../../../hooks/useUserData";
import { useSendPurchaseEvent } from "../../../hooks/useSendPurchaseEvent";
import { useRefetchAccountAndRelease } from "../../../hooks/useRefetchAccountAndRelease";
import { pendingAuthPaymentConfigStore } from "../../../hookstate/pendingAuthPaymentConfigStore";
import { FunnelOnSuccess } from "../../funnel/types";

import { SubscriptionOfferV2Type } from "../types";

type PurchaseMeta = {
  congrats_image?: string;
  congrats_text?: string;
  shelf_cta?: boolean;
};

type UseSubscriptionPurchaseV2Args = {
  activeType: "yearly" | "monthly" | undefined;
  yearlySub: SubscriptionOfferV2Type | undefined;
  monthlySub: SubscriptionOfferV2Type | undefined;
  promoCode: string;
  onFunnelSuccess?: FunnelOnSuccess;
};

export function useSubscriptionPurchaseV2({
  activeType,
  yearlySub,
  monthlySub,
  promoCode,
  onFunnelSuccess,
}: UseSubscriptionPurchaseV2Args) {
  const { userData } = useUserData();
  const { sendPurchaseEvent } = useSendPurchaseEvent();
  const refetchQueries = useRefetchAccountAndRelease();

  const getPurchasePayload = useCallback(
    (stripePaymentConfig: StripePaymentConfigTypes) => {
      const { access_token_type_id, current_price, promo_code } =
        (activeType === "yearly" ? yearlySub : monthlySub) ?? {};
      return {
        ...stripePaymentConfig,
        accessTokenTypeId: access_token_type_id ?? 0,
        totalMinorUnits: Math.round((current_price ?? 0) * 100),
        incentiveCode: promoCode.length ? promoCode : promo_code,
      };
    },
    [activeType, yearlySub, monthlySub, promoCode],
  );

  const onSuccess = async (
    response?: AxiosResponse<ServerResponse<unknown, PurchaseMeta>>,
  ) => {
    setWaiting(true);

    const purchasedSubscription =
      activeType === "yearly" ? yearlySub : monthlySub;
    if (purchasedSubscription) {
      sendPurchaseEvent({
        value: purchasedSubscription.current_price,
        currency: "USD",
        itemId: purchasedSubscription.access_token_type_id,
        itemName: `${purchasedSubscription.billing_frequency} subscription`,
      });
    }

    const meta = response?.data?.meta;
    const congratsConfig = meta
      ? {
          customCongratsImage: meta.congrats_image,
          customCongratsText: meta.congrats_text,
        }
      : undefined;

    const data = await refetchQueries();
    onFunnelSuccess?.({
      data,
      options: {
        didPurchaseSubscription: true,
        purchasedYearly: activeType === "yearly",
        componentProps: congratsConfig,
      },
    });
  };

  const onError = (error: Error) => {
    let errorMessage = t(
      "components.payments.subscription.subscriptionDeclined",
    );

    if (axios.isAxiosError<ServerResponse<string>>(error)) {
      errorMessage = error.response?.data?.payload.results || errorMessage;
    }
    return errorMessage;
  };

  const { mutate, waiting, setWaiting, failedCard, cardValidationError } =
    usePaymentMutation(webPurchase, onSuccess, onError);

  const submittingRef = useRef(false);
  if (!waiting) submittingRef.current = false;

  const submitPurchase = useCallback(
    (stripePaymentConfig: StripePaymentConfigTypes) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      if (!userData) {
        pendingAuthPaymentConfigStore.set(
          getPurchasePayload(stripePaymentConfig),
        );
      } else {
        mutate(getPurchasePayload(stripePaymentConfig));
      }
    },
    [getPurchasePayload, mutate, userData],
  );

  useEffect(() => {
    const pendingConfig = pendingAuthPaymentConfigStore.get({
      noproxy: true,
    });
    if (userData?.gold && pendingConfig) {
      pendingAuthPaymentConfigStore.set(undefined);

      onSuccess();
    } else if (pendingConfig) {
      mutate(pendingConfig);
      pendingAuthPaymentConfigStore.set(undefined);
    }
  }, [userData]);

  useEffect(() => {
    return () => {
      pendingAuthPaymentConfigStore.set(undefined);
    };
  }, []);

  return {
    submitPurchase,
    waiting,
    failedCard,
    cardValidationError,
  };
}
